import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loading } from '@xanectech/components/shared';

import { AppLayout } from '@xanectech/components/layout';
import { useTrueIDAppConfig } from './hooks/useTrueIDAppConfig';

import { GlobalUnlockDialog } from './components/GlobalUnlockDialog';
import { UnlockStatusButton } from './components/UnlockStatusButton';

// Lazy-loaded pages for code splitting
const NotFoundPageLazy = lazy(() =>
  import('@xanectech/components/shared').then((m) => ({
    default: m.NotFoundPage,
  })),
);

const AuthPageLazy = lazy(() =>
  import('@xanectech/auth').then((m) => ({ default: m.AuthPage })),
);
const LogoutPageLazy = lazy(() =>
  import('./pages/LogoutPage').then((m) => ({ default: m.LogoutPage })),
);

const SignupPageLazy = lazy(() =>
  import('./pages/Signup').then((m) => ({ default: m.SignupPage })),
);

const DashboardPageLazy = lazy(() =>
  import('./pages/Dashboard').then((m) => ({
    default: m.DashboardPage,
  })),
);

const SettingsPageLazy = lazy(() =>
  import('./pages/Settings').then((m) => ({
    default: m.SettingsPage,
  })),
);

const AnalyticsPageLazy = lazy(() =>
  import('./pages/AnalyticsPage').then((m) => ({
    default: m.AnalyticsPage,
  })),
);

const PersonalDataPageLazy = lazy(() =>
  import('./pages/PersonalDataPage').then((m) => ({
    default: m.PersonalDataPage,
  })),
);

const MessagesPageLazy = lazy(() =>
  import('./pages/MessagesPage').then((m) => ({
    default: m.MessagesPage,
  })),
);

const QRCodeScanPageLazy = lazy(() =>
  import('./pages/QRScanPage').then((m) => ({
    default: m.QRScanPage,
  })),
);

const SharedDataHistoryPageLazy = lazy(() =>
  import('./pages/SharedDataHistoryPage').then((m) => ({
    default: m.SharedDataHistoryPage,
  })),
);

const DocumentsPageLazy = lazy(() =>
  import('./pages/DocumentsPage').then((m) => ({
    default: m.DocumentsPage,
  })),
);

const ImportDataPageLazy = lazy(() =>
  import('./pages/ImportDataPage').then((m) => ({
    default: m.ImportDataPage,
  })),
);

const ContractSubmissionPage = lazy(() =>
  import('./pages/ContractSubmissionPage').then((m) => ({
    default: m.ContractSubmissionPage,
  })),
);

// Loading fallback component
const RouteLoadingFallback = () => <Loading spinner='Xanec' />;

/**
 * Main routing component for TrueID.Locker app
 * Uses outlet pattern with AppLayout for consistent navigation
 */
export function TrueIDRoutes() {
  const appConfig = useTrueIDAppConfig();

  // If unauthenticated, show public routes only
  if (appConfig.status === 'unauthenticated') {
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path='/' element={<Navigate to='/auth' replace />} />
          <Route
            path='/auth'
            element={
              <AuthPageLazy allowSignup={false} redirectPath='/dashboard' />
            }
          />
          <Route path='/dashboard' element={<Navigate to='/auth' replace />} />
          <Route path='/signup' element={<SignupPageLazy />} />
          <Route path='/logout' element={<LogoutPageLazy />} />

          {/* Contract Submission Routes */}
          <Route path='submit/:token' element={<ContractSubmissionPage />} />

          <Route path='*' element={<NotFoundPageLazy />} />
        </Routes>
      </Suspense>
    );
  }

  // If loading, show loading screen
  if (appConfig.status === 'loading') {
    return <Loading spinner='Xanec' />;
  }

  // Authenticated routes with AppLayout
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <GlobalUnlockDialog />

      <Routes>
        {/* Public routes */}
        <Route path='/' element={<Navigate to='/auth' replace />} />
        <Route
          path='/auth'
          element={
            <AuthPageLazy allowSignup={false} redirectPath='/dashboard' />
          }
        />
        <Route path='/signup' element={<SignupPageLazy />} />
        <Route path='/logout' element={<LogoutPageLazy />} />

        {/* Protected routes with AppLayout */}
        <Route
          element={
            <AppLayout
              config={appConfig.config}
              appHeaderComponent={UnlockStatusButton}
            />
          }
        >
          <Route path='/dashboard' element={<DashboardPageLazy />} />
          <Route path='/settings' element={<SettingsPageLazy />} />
          <Route path='/data' element={<PersonalDataPageLazy />} />
          <Route path='/analytics' element={<AnalyticsPageLazy />} />
          <Route path='/messages' element={<MessagesPageLazy />} />
          <Route path='/scan' element={<QRCodeScanPageLazy />} />
          <Route path='/documents' element={<DocumentsPageLazy />} />
          <Route path='/import' element={<ImportDataPageLazy />} />
          <Route
            path='/data-contracts'
            element={<SharedDataHistoryPageLazy />}
          />

          {/* Contract Submission Routes */}
          <Route path='submit/:token' element={<ContractSubmissionPage />} />

          {/* 404 within app */}
          <Route path='*' element={<NotFoundPageLazy showDashboard />} />
        </Route>
        {/* 404 */}
        <Route path='*' element={<NotFoundPageLazy showHome showProducts />} />
      </Routes>
    </Suspense>
  );
}

// Deprecated: Legacy export for backwards compatibility
// This function is deprecated and will be removed in a future version
export const routes = () => {
  return [];
};

export type RouteType = {
  path: string;
  element: React.ReactElement;
};
